import React, { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Box, LinearProgress, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  InsertDriveFileOutlined,
} from '@mui/icons-material';
import parse from 'csv-parse';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations';
import {
  generateUpdatesFromCSV,
  getDragClassName,
  validateCSVStructure,
  type ColumnDefinition,
  type CostTableRowForUpload,
} from '@Utils/costUploadUtils';
import {
  Container,
  DropzoneInfoContainerFull,
  StyledDropzoneArea,
  StyledDropzoneFileRowContainer,
} from './styles/FileDropzone.styles';
import { UpArrowCircle } from './icons';
import {
  StylovyzeDialog,
  error as toastError,
  success as toastSuccess,
} from '@innovyze/stylovyze';
import { useCostTableTracking } from '@Hooks/useCostTableTracking';

type DialogStage = 'idle' | 'processing' | 'complete' | 'error';

interface CostTableData {
  id: string;
  assetGroupSet: string;
  description: string;
  assetCount: number;
  unitCost: number;
  isUpdated?: boolean;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any -- needed for compatibility with CostTableDetailData which has additional fields
}

interface CostUploadDialogProps {
  expectedColumns?: ColumnDefinition[];
  allowAdditionalColumns?: boolean;
  existingData?: CostTableData[];
  keyField?: string;
  comparableFields?: string[];
  onUpdatedValuesChange?: (updates: Record<string, CostTableData>) => void;
  open: boolean;
  onClose: () => void;
  costTableId?: string;
}

const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

const CostUploadDialog: React.FC<CostUploadDialogProps> = ({
  expectedColumns,
  allowAdditionalColumns = true,
  existingData,
  keyField = 'id',
  comparableFields = [],
  onUpdatedValuesChange,
  open,
  onClose,
  costTableId,
}) => {
  const { t } = useGlobalization();
  const dispatch = useDispatch();
  const { trackCsvUploadStart, trackCsvUploadComplete, trackCsvUploadFailed } =
    useCostTableTracking();

  const [stage, setStage] = useState<DialogStage>('idle');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<Record<
    string,
    CostTableData
  > | null>(null);
  const [isDropRejected, setIsDropRejected] = useState(false);

  const resetState = useCallback(() => {
    setStage('idle');
    setUploadedFile(null);
    setTotalRows(0);
    setUpdatedCount(0);
    setValidationErrors([]);
    setPendingUpdates(null);
    setIsDropRejected(false);
  }, []);

  const processFile = async (file: File) => {
    setUploadedFile(file);
    setStage('processing');
    trackCsvUploadStart({ costtableid: costTableId ?? '' });

    try {
      const fileContent = await file.text();

      parse(
        fileContent,
        { columns: true, trim: true, skipEmptyLines: false },
        (err, output) => {
          if (err) {
            setValidationErrors([`Failed to parse CSV: ${err.message}`]);
            setStage('error');
            trackCsvUploadFailed({
              costtableid: costTableId ?? '',
              error: err.message,
            });
            return;
          }

          const validation = validateCSVStructure(
            output,
            file.name,
            expectedColumns,
            allowAdditionalColumns
          );

          if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setStage('error');
            dispatch(toastError(t('Unable to update costs via CSV Upload')));
            trackCsvUploadFailed({
              costtableid: costTableId ?? '',
              error: 'Validation failed',
            });
            return;
          }

          const rowCount = validation.data?.rows.length ?? 0;
          setTotalRows(rowCount);

          let updates: Record<string, CostTableData> = {};
          if (existingData && comparableFields.length > 0 && validation.data) {
            updates = generateUpdatesFromCSV(
              validation.data,
              existingData as CostTableRowForUpload[],
              keyField,
              comparableFields
            );
          }
          setPendingUpdates(updates);
          setUpdatedCount(Object.keys(updates).length);
          setStage('complete');
        }
      );
    } catch (err) {
      setValidationErrors([`Error reading file: ${err}`]);
      setStage('error');
      dispatch(toastError(t('Unable to update costs via CSV Upload')));
      trackCsvUploadFailed({
        costtableid: costTableId ?? '',
        error: String(err),
      });
    }
  };

  const onDropAccepted = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setIsDropRejected(true);
      return;
    }
    setIsDropRejected(false);
    processFile(file);
  };

  const onDropRejected = () => {
    setIsDropRejected(true);
  };

  // Delay reset until after the close animation so the dialog doesn't flash back to idle.
  // If the dialog reopens before 300ms, the cleanup runs — cancel the timer and reset
  // immediately so the dialog always opens in a clean idle state.
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(resetState, 300);
      return () => {
        clearTimeout(timer);
        resetState();
      };
    }
  }, [open, resetState]);

  const handleConfirm = () => {
    if (stage === 'complete') {
      if (pendingUpdates && Object.keys(pendingUpdates).length > 0) {
        onUpdatedValuesChange?.(pendingUpdates);
        dispatch(toastSuccess(t('Costs uploaded via CSV successfully')));
        trackCsvUploadComplete({
          costtableid: costTableId ?? '',
          rowcount: Object.keys(pendingUpdates).length,
        });
      }
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const displayColumns = (expectedColumns ?? []).filter(
    (c): c is ColumnDefinition & { displayName: string } => !!c.displayName
  );

  const renderHeader = () => (
    <>
      <Typography sx={{ mb: 2 }}>
        {t(
          'Select a completed cost table CSV file to upload. The file should match the exported format with the same column headers and asset group names.'
        )}
      </Typography>
      {displayColumns.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <span>ⓘ</span>
          <span>
            {t('Expected CSV format:')}
            <br />
            {displayColumns.map((col, i) => (
              <React.Fragment key={col.header}>
                {'• '}
                {col.displayName}
                {i < displayColumns.length - 1 && <br />}
              </React.Fragment>
            ))}
            <br />
            {t(
              'Tip: Export the current table first to get a template with all group names pre-filled.'
            )}
          </span>
        </Typography>
      )}
    </>
  );

  const renderIdle = () => (
    <>
      {renderHeader()}
      <Container style={{ marginTop: '10px', marginBottom: '20px', flex: 1 }}>
        <Dropzone
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          onDragEnter={() => setIsDropRejected(false)}
          accept={['.csv', 'text/csv', 'application/vnd.ms-excel']}
          multiple={false}
          maxSize={10 * 1024 * 1024}>
          {({
            getRootProps,
            getInputProps,
            isDragActive,
            isDragAccept,
            isDragReject,
          }) => (
            <StyledDropzoneArea {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <DropzoneInfoContainerFull
                className={getDragClassName(
                  isDragActive,
                  isDragAccept,
                  isDragReject
                )}>
                <StyledDropzoneFileRowContainer>
                  <UpArrowCircle
                    fill={
                      isDragReject || isDropRejected ? '#AA0000' : '#C9D6DC'
                    }
                  />
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, mt: 1, mb: 0 }}>
                    {isDragReject || isDropRejected
                      ? t(
                          "The file you're trying to import is in an invalid format",
                          { context: 'File dropzone' }
                        )
                      : t('Drag and drop CSV files here or click to browse', {
                          context: 'File dropzone',
                        })}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0 }}>
                    {t('Supported file type: CSV')}
                  </Typography>
                </StyledDropzoneFileRowContainer>
              </DropzoneInfoContainerFull>
            </StyledDropzoneArea>
          )}
        </Dropzone>
      </Container>
    </>
  );

  const renderProcessing = () => (
    <>
      {renderHeader()}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mt: 1,
              mb: 1,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}>
              <InsertDriveFileOutlined
                sx={{ color: 'text.secondary', mt: '2px' }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {uploadedFile?.name ?? ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {uploadedFile ? formatFileSize(uploadedFile.size) : ''}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 36, textAlign: 'right' }}>
                {t('Processing...')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderComplete = () => {
    const unchanged = totalRows - updatedCount;
    return (
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            pb: 1,
            color: 'success.main',
          }}>
          <CheckCircleOutline />
          <Typography
            variant="body1"
            sx={{ color: 'success.main', fontWeight: 500 }}>
            {t('Processing complete')}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('{{count}} costs processed', { count: totalRows })}
        </Typography>
        {comparableFields.length > 0 && (
          <>
            <Typography variant="body2">
              {t('{{count}} updated', { count: updatedCount })}
            </Typography>
            <Typography variant="body2">
              {t('{{count}} unchanged', { count: unchanged })}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const renderError = () => (
    <>
      {renderHeader()}
      {validationErrors.map(err => (
        <Typography key={err} sx={{ mt: 1 }} color="error">
          {err}
        </Typography>
      ))}
    </>
  );

  const renderContent = () => {
    switch (stage) {
      case 'processing':
        return renderProcessing();
      case 'complete':
        return renderComplete();
      case 'error':
        return renderError();
      default:
        return renderIdle();
    }
  };

  const isDone = stage === 'complete' || stage === 'error';

  return (
    <StylovyzeDialog
      dialogProps={{ maxWidth: 'md' }}
      onDialogClose={() => void 0}
      open={open}
      title={t('Upload Costs')}
      cancelText={isDone ? '' : t('Cancel')}
      onCancel={isDone ? handleConfirm : handleCancel}
      cancelButtonProps={isDone ? { style: { display: 'none' } } : {}}
      confirmText={isDone ? t('Done') : undefined}
      onConfirm={handleConfirm}
      confirmButtonProps={isDone ? {} : { style: { display: 'none' } }}>
      <div style={{ width: '50em', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </div>
    </StylovyzeDialog>
  );
};

export default CostUploadDialog;
